import { type ReactNode, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { TeleportTarget } from './TeleportTarget.js';
import { useStableHost } from './useStableHost.js';

/**
 * Creates a stable DOM element whose React subtree
 * survives parent re-mounts. Call `Source` once (outside any conditional
 * branches) to declare *what* to render, and place `Target` wherever the
 * content should appear in the DOM.
 *
 * Because the host `<div>` is created once (via `useStableHost`) and only
 * moved via `appendChild` in a `useLayoutEffect`, React never unmounts the
 * subtree when the layout switches between split / overlay / collapsed /
 * drawer modes.
 *
 * @example
 * ```tsx
 * const content = useTeleport();
 * const panel = useTeleport();
 *
 * // Always rendered – defines *what* goes into each slot.
 * <content.Source>main area</content.Source>
 * <panel.Source>sidebar / details</panel.Source>
 *
 * // Placed in the appropriate layout branch.
 * {mode === 'split' ? <content.Target /> : null}
 * {mode === 'drawer' ? <panel.Target /> : null}
 * ```
 *
 * @internal
 */
export function useTeleport() {
  const host = useStableHost();

  return useMemo(
    () => ({
      // `Source` and `Target` are declared in `useMemo` on purpose.
      // `host` is stable per hook instance, so `[host]` keeps these component
      // identities stable across re-renders. If this were changed to depend
      // on something unstable, consumers would see new component types and
      // React would remount their subtrees.

      /**
       * Renders `children` into `host` via a React portal.
       * Must be rendered exactly **once** and must not be rendered conditionally.
       */
      Source: function TeleportSource({ children }: { children: ReactNode }) {
        return createPortal(children, host);
      },

      /**
       * Places the `host` element in the DOM at the point where this
       * component is mounted. When `Target` moves between different parents
       * the DOM node is re-appended without React unmounting it.
       *
       * Delegates to `TeleportTarget` — the same component used by call sites
       * that manage their own host node directly (e.g. `PageLayout`).
       */
      Target: function TeleportTarget_() {
        return <TeleportTarget host={host} />;
      },
    }),
    [host],
  );
}
